import { useState } from 'react';

import { tokens } from '@opendoor/tokens-studio/dist/bricks-next/tokens/Components';
import type { CheckedState } from '@tamagui/checkbox-headless';
import { useProps } from '@tamagui/core';

import { Box } from '../../Box';
import { TrackableContainer } from '../../shared/TrackableContainer';
import { useInputId, useInputState } from '../shared/hooks';
import { CheckboxRadioGroupError } from './shared/CheckboxRadioGroupError';
import { CheckboxRadioGroupLabel } from './shared/CheckboxRadioGroupLabel';
import type { SharedCheckboxRadioGroupProps } from './shared/types';
import { SingleCheckbox } from './SingleCheckbox';

const { radioCheckbox: checkboxTokens } = tokens;

export type CheckboxGroupProps = SharedCheckboxRadioGroupProps & {
  /**
   * The initial checked values for the checkbox group
   */
  initialCheckedValues?: Array<string>;
  /**
   * Provides the parent component with a way to view the checked state of each checkbox
   */
  onPress?: (checkedValues: Array<string>) => void;
  /**
   * A special value representing the "None of the Above" option.
   * When this option is selected, all other options in the group are deselected.
   * If any other option is selected, the "None of the Above" option will be deselected.
   */
  noneOfTheAboveValue?: string;
};

/**
 * Note: To use just one Checkbox use SingleCheckbox instead <br>
 * The CheckboxGroup component allows users to select multiple options from a list of predefined alternatives.
 * Each individual checkbox is build with [Tamagui's headless checkbox](https://tamagui.dev/docs/components/checkbox?type=headless). This is a stateful component that keeps track of selected values.
 *
 *
 *
 * **Meaningful Properties:**
 * - The initialCheckedValues prop is an array - where you can pass in strings of initial checked values
 * - Pass an onPress function to get access to the selected values from the parent component
 * - Pass errorMessage or invalid if you want the error state to show up
 * - Pass disabled to disable the checkbox group
 * - The required prop is used to the required design (an asterisk next to the title) - checkbox group does not validate required for you. Please validate in the parent component's onSubmit function
 *
 * **Analytics:**
 * - Click Events: Checkbox click events will be tracked as ("checkboxClickEvent"), so `analyticsName` is required
 * - Impression Events: If you want to track impression events as ("checkboxViewEvent"), pass
 * `analyticsName` and `trackImpression` if you want to track the impression event
 *
 * **Accessibility:**
 *
 * Keyboard Interaction:
 * - When the checkbox has focus, pressing the [space](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/checkbox_role#keyboard_interactions) key changes the state of the checkbox
 * - The tab key moves between checkboxes
 *
 *
 * Checkbox Group:
 * - Are grouped together with a 'group` role to indicate which ones affect the same value.
 * - Are defined by giving each of the checkboxes in the group the same name
 * - Uses an aria-labelledby (the title) to describe the checkbox group
 *
 *
 * Checkbox:
 * - Has role [checkbox](https://w3c.github.io/aria/#checkbox)
 * - Focus indicator is visible and has sufficient contrast
 * - Value attribute attached
 * - [Aria-checked](https://developer.mozilla.org/en-US/docs/web/Accessibility/ARIA/Attributes/aria-checked) attribute updates based on if its checked or not
 * - Has an accessible label because the visible text content is contained within the element with role `checkbox`
 */
export const CheckboxGroup = ({
  analyticsName,
  trackImpression,
  options,
  title,
  supportText,
  errorText,
  invalid,
  disabled,
  initialCheckedValues,
  onPress,
  id,
  noneOfTheAboveValue,
  required,
  ...props
}: CheckboxGroupProps) => {
  const { size = 'medium' } = useProps(props);

  const groupLabelId = useInputId(id);

  const { state } = useInputState({
    errorText,
    invalid,
    disabled,
  });

  // allows us to keep track of the checked values so the parent component can access them from the optional onClick function
  const [checkedValues, setCheckedValues] = useState<Array<string>>(initialCheckedValues ?? []);
  const handleCheckedChange = (value: string, _checked: CheckedState) => {
    setCheckedValues((currentCheckedValues) => {
      const newCheckedValues =
        value === noneOfTheAboveValue
          ? currentCheckedValues.includes(noneOfTheAboveValue)
            ? []
            : [noneOfTheAboveValue]
          : currentCheckedValues.includes(value)
            ? currentCheckedValues.filter((val: string) => val !== value)
            : [...currentCheckedValues.filter((val: string) => val !== noneOfTheAboveValue), value];

      onPress?.(newCheckedValues);
      return newCheckedValues;
    });
  };

  return (
    <TrackableContainer
      analyticsName={analyticsName}
      trackImpression={trackImpression}
      gap={checkboxTokens.padding.titleRowGap[size]}
      flexGrow={1}
      eventName="checkboxViewEvent"
    >
      <CheckboxRadioGroupLabel
        size={size}
        title={title}
        supportText={supportText}
        id={`${groupLabelId}-label`}
        state={state}
        required={required}
      />
      <Box
        role="group"
        aria-labelledby={`${groupLabelId}-label`}
        gap={checkboxTokens.padding.rowGap[size]}
      >
        {options.map((option) => {
          const defaultChecked = initialCheckedValues?.includes(option.value);
          const isNoneOfTheAbove = option.value === noneOfTheAboveValue;
          return (
            <SingleCheckbox
              {...props}
              // do not pass required down to the SingleCheckbox
              key={option.value}
              analyticsName={analyticsName}
              invalid={!!errorText || invalid}
              disabled={disabled}
              label={option.label}
              subtitle={option.subtitle}
              size={size}
              value={option.value}
              onPress={handleCheckedChange}
              state={state}
              defaultChecked={defaultChecked}
              checked={checkedValues.includes(option.value)}
              id={`${groupLabelId}-${option.value}`}
              name={id}
              hiddenCheckIcon={isNoneOfTheAbove}
              renderLabel={option.renderLabel}
            />
          );
        })}
      </Box>
      <CheckboxRadioGroupError errorText={errorText} />
    </TrackableContainer>
  );
};
